import React, { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, StatusBadge, Btn } from "@/lib/ui";
import { HistorySidebar } from "@/components/HistorySidebar";
import { Send, Cpu, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "Give me today's GGG overview.",
  "Review important pending items.",
  "Ask Finance AI for a summary.",
  "Bring Marketing and Strategy into this discussion.",
  "Show me what requires approval.",
];
const LS_KEY = "ggg:chief:activeConv";

export default function ChiefOfStaff() {
  const api = useApi();
  const qc = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(() => localStorage.getItem(LS_KEY) || null);
  const [showArchived, setShowArchived] = useState(false);
  const endRef = useRef(null);
  const bootstrapped = useRef(false);

  const convs = useQuery({ queryKey: ["chief-convs", showArchived], queryFn: () => api.get(`/chief/conversations${showArchived ? "?include_archived=true" : ""}`) });

  // On first load, if no conversation is selected, restore the most recent one.
  useEffect(() => {
    if (bootstrapped.current || !convs.data) return;
    bootstrapped.current = true;
    if (!convId && convs.data.length > 0) setConvId(convs.data[0].id);
  }, [convs.data, convId]);

  // Load the selected conversation's messages from the backend (resume).
  useEffect(() => {
    if (!convId) { setMessages([]); localStorage.removeItem(LS_KEY); return; }
    localStorage.setItem(LS_KEY, convId);
    let cancelled = false;
    api.get(`/chief/conversations/${convId}`)
      .then((c) => { if (!cancelled) setMessages((c.messages || []).map((m, i) => ({ id: `${convId}-${i}`, ...m }))); })
      .catch(() => { if (!cancelled) { setConvId(null); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = useMutation({
    mutationFn: (message) => api.post("/chief/message", { message, conversation_id: convId }),
    onSuccess: (r) => {
      if (!convId) { setConvId(r.conversation_id); localStorage.setItem(LS_KEY, r.conversation_id); }
      setMessages((m) => [...m, { id: `${Date.now()}-a`, role: "assistant", content: r.reply, consulted: r.consulted, tokens: r.tokens }]);
      qc.invalidateQueries({ queryKey: ["chief-convs"] });
    },
    onError: (e) => {
      toast.error(e.message);
      setMessages((m) => [...m, { id: `${Date.now()}-e`, role: "assistant", content: `⚠ ${e.message}`, error: true }]);
    },
  });

  const submit = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || send.isPending) return;
    setMessages((m) => [...m, { id: `${Date.now()}-u`, role: "user", content: msg }]);
    setInput("");
    send.mutate(msg);
  };

  const newConversation = () => { setConvId(null); setMessages([]); localStorage.removeItem(LS_KEY); };
  const rename = (id, title) => api.patch(`/chief/conversations/${id}`, { title }).then(() => qc.invalidateQueries({ queryKey: ["chief-convs"] }));
  const archive = (id, archived = true) => api.patch(`/chief/conversations/${id}`, { archived }).then(() => { toast.success(archived ? "Archived" : "Restored"); if (archived && id === convId) newConversation(); qc.invalidateQueries({ queryKey: ["chief-convs"] }); });
  const del = (id) => api.del(`/chief/conversations/${id}`).then(() => { toast.success("Deleted"); if (id === convId) newConversation(); qc.invalidateQueries({ queryKey: ["chief-convs"] }); });

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <PageHeader kicker="GGG-01" title="Chief of Staff">
        <StatusBadge tone="proc" dot="pulse">MeshAPI Live</StatusBadge>
      </PageHeader>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-4 min-h-0">
        <div className="hidden lg:block min-h-0">
          <HistorySidebar
            items={convs.data || []} activeId={convId} loading={convs.isLoading}
            onSelect={setConvId} onNew={newConversation} onRename={rename} onArchive={archive} onDelete={del}
            newLabel="New Conversation" heading="Conversations" testidPrefix="chief"
            showArchived={showArchived} onToggleArchived={setShowArchived}
          />
        </div>

        <div className="flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto border border-border rounded-md bg-surface/50 p-4 space-y-4">
            {messages.length === 0 && !send.isPending && (
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 text-primary mb-4">
                  <Sparkles className="h-5 w-5" />
                  <span className="font-head text-lg font-bold">Your Group AI Chief of Staff</span>
                </div>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                  Ask anything. I route work to the right executive agents — Finance, Strategy, Marketing,
                  Operations and more — then synthesise a single answer for you.
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="chief-suggestion" onClick={() => submit(s)}
                      className="text-xs border border-border rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={m.id || i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div className={cn(
                  "max-w-[80%] rounded-md px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed",
                  m.role === "user" ? "bg-primary/15 border border-primary/30"
                    : cn("bg-surface-elevated border-l-2 border-ggg pl-4", m.error && "border-err bg-err/5"),
                )}>
                  {m.role === "assistant" && (
                    <div className="text-[10px] font-mono uppercase tracking-wider text-ggg mb-1.5">Chief of Staff</div>
                  )}
                  {m.content}
                  {m.consulted?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2 border-t border-border/50">
                      {m.consulted.map((c) => (
                        <span key={c.code} className="text-[10px] font-mono text-muted-foreground border border-border rounded-full px-2 py-0.5">
                          consulted {c.code}
                        </span>
                      ))}
                      {m.tokens != null && <span className="text-[10px] font-mono text-muted-foreground">· {m.tokens} tok</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {send.isPending && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Cpu className="h-4 w-4 animate-spin text-proc" /> Consulting executives…
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="mt-3 flex gap-2">
            <input data-testid="chief-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && submit()}
              placeholder="Ask your Chief of Staff…"
              className="flex-1 h-11 rounded-md border border-border bg-surface px-4 text-sm focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none" />
            <Btn onClick={() => submit()} disabled={send.isPending} className="h-11 px-5" data-testid="chief-send">
              <Send className="h-4 w-4" /> Send
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
